<template>
  <div :class="selected_report?.story_board || print_mode ? 'px-print' : 'container'">
    <template v-if="!selected_report?.story_board">
      <h2 class="mt-5">TCFD Index</h2>
    </template>

    <TableLoading v-if="tcfd_contents.length === 0" />

    <template>
      <div class="mb-5">
        <template v-if="show_table_header">
          <b-row class="mt-3">
            <b-col cols="3">
              {{ lang != 'ENG' ? '보고 지표' : 'Disclosures' }}
            </b-col>
            <b-col cols="3">
              {{ lang != 'ENG' ? '보고 내용' : 'Disclosure Contents' }}
            </b-col>
            <b-col cols="6" class="text-right">
              <b-icon icon="table" class="mr-1" />
              {{ lang != 'ENG' ? '데이터 테이블' : 'Data Tables' }}
              <b-icon icon="file-earmark-text" class="ml-3 mr-1" />{{
                lang != 'ENG' ? '문서' : 'Files'
              }}
              <b-icon icon="link-45deg" class="ml-3 mr-1" />{{
                lang != 'ENG' ? '외부 링크' : 'URLs'
              }}
              <b-icon icon="briefcase" class="ml-3 mr-1" />
              {{ lang != 'ENG' ? '비즈니스 사례' : 'Business Cases' }}
            </b-col>
          </b-row>
        </template>

        <div
          v-for="(category_info, category_idx) in tcfd_contents"
          :id="!print_mode ? 'rp' + category_info?.category_code : ''"
          :key="category_idx"
          class="mb-5"
        >
          <div v-if="category_info.indicators.length > 0" class="d-flex">
            <div class="flex-grow-1 py-1 pl-1" :style="main_color_sub">
              <span class="f-140 fw-600">
                {{ category_idx + 1 }}.
                {{ lang != 'ENG' ? category_info.category_name : category_info.category_name_e }}
              </span>
              <span class="gray-500 f-90 fw-400 ml-1"></span>
            </div>
          </div>

          <div
            v-for="disclosure in category_info.indicators"
            :id="!print_mode ? 'w' + disclosure.tcfd_code : ''"
            :key="disclosure.disclosure_id"
            class="d-flex py-3 border-bottom"
          >
            <div :class="print_mode ? 'col-6' : 'col-3'">
              <div class="fw-500">{{ disclosure.tcfd_code }}</div>
              <div v-if="lang !== 'ENG'">
                <p>{{ disclosure.head_message }}</p>
                <p>{{ disclosure.indicator_name }}</p>
                <p class="pb-3">{{ disclosure.topic }}</p>
                <div class="fw-400">{{ disclosure.metric }}</div>
              </div>

              <div v-else>
                <p>{{ disclosure.head_message_e }}</p>
                <p>{{ disclosure.indicator_name_e }}</p>
                <p class="pb-3">{{ disclosure.topic }}</p>
                <div class="fw-400">{{ disclosure.metric }}</div>
              </div>
            </div>

            <div :class="print_mode ? 'col-6' : 'col-9'">
              <p
                v-if="lang === 'KOR' && disclosure.direct_report"
                class="page-no-break-inside"
                style="word-wrap: break-word; white-space: pre-line"
              >
                <span v-html="disclosure.direct_report"></span>
              </p>
              <p
                v-else-if="
                  lang !== 'KOR' && (disclosure.direct_report_em || disclosure.direct_report_ea)
                "
                class="page-no-break-inside"
                style="word-wrap: break-word; white-space: pre-line"
              >
                <span
                  v-html="
                    disclosure.direct_report_em
                      ? disclosure.direct_report_em
                      : disclosure.direct_report_ea
                  "
                ></span>
              </p>
              <!-- data_tables 넣을자리 -->
              <div
                v-for="data_table_id in disclosure.data_tables"
                :key="'data_table_' + data_table_id"
                class="d-print-none"
              >
                <TcfdDataTable
                  :print_mode="print_mode"
                  :table_mode="table_mode"
                  :data_table_id="data_table_id"
                  :modal_table="modal_table"
                  :lazy_load="false"
                  :table_style="table_style"
                  :show_table_line="show_table_line"
                  :show_table_outline="show_table_outline"
                  :table_header_color="table_header_color"
                  :table_header_font_color="table_header_font_color"
                />
              </div>

              <!-- documents 넣을자리 -->
              <div class="d-flex flex-column d-print-none">
                <span v-for="document_id in disclosure.documents" :key="'document_' + document_id">
                  <Document :document_id="document_id" :lazy_load="false" />
                </span>
              </div>

              <!-- business_cases 넣을자리 -->
              <div class="d-flex flex-column d-print-none">
                <span
                  v-for="business_case_id in disclosure.business_cases"
                  :key="'business_case_' + business_case_id"
                >
                  <BusinessCase
                    :business_case_id="business_case_id"
                    :business_case_mode="business_case_mode"
                  />
                </span>
              </div>

              <span
                v-for="chart_id in disclosure.charts"
                :key="'chart_' + chart_id"
                class="d-print-none"
              >
                <ChartCanvas
                  v-if="chart_id !== ''"
                  :key="`chart_${chart_id}`"
                  :chart_id="chart_id"
                  :print_mode="print_mode"
                  :viewer_mode="true"
                  class="mb-5"
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Document from './Document.vue';
import BusinessCase from './BusinessCase.vue';
import { nan_table } from '@/components/util';
import TableLoading from './TableLoading.vue';
import ChartCanvas from '@/components/writing_report/chart_subs/ChartCanvas.vue';
import TcfdDataTable from './TcfdDataTable.vue';
import { gri_mixin } from '@/components/writing_report/write_report_subs/gri_mixin.js';

export default {
  name: 'TCFDTable',
  components: {
    TcfdDataTable,
    Document,
    BusinessCase,
    TableLoading,
    ChartCanvas
  },
  mixins: [gri_mixin],
  props: {
    modal_table: { type: Boolean, default: false },
    reload: { type: Boolean, default: false },
    print_mode: { type: Boolean, default: false },
    business_case_mode: { type: String, default: 'popup' },
    table_mode: { type: String, default: 'accordion' },
    material_style_option: { type: String, default: 'fill' },

    table_style: { type: String, default: 'basic_table' },
    show_table_line: { type: Boolean, default: true },
    show_table_outline: { type: Boolean, default: false },
    table_header_color: { type: String, default: '#dedede' },
    table_header_font_color: { type: String, default: '#fffff' }
  },
  data() {
    return {
      show_data_table: {},
      show_business_case: {},
      low_height: false,
      selected_indicator: null,
      show_table_header: true,
      nan_table
    };
  },
  mounted() {
    this.fetchSelectedTcfd(this.current_report_code);
  },
  computed: {
    ...mapState('report_contents', ['tcfd_contents']),
    ...mapState('report_list', ['lang', 'view_mode']),
    ...mapGetters('report_list', ['selected_report', 'current_report_code']),
    main_color() {
      return `color:` + this.selected_report.main_color;
    },
    main_color_sub() {
      if (this.material_style_option === 'fill') {
        return `background-color: ${this.selected_report.main_color}; color: white; border: 1px solid white; `;
      } else if (this.material_style_option === 'outline') {
        return (
          `color:` +
          this.selected_report.main_color +
          `; border-top: 1px solid ` +
          this.selected_report.main_color +
          `!important` +
          `; border-bottom: 1px solid ` +
          this.selected_report.main_color
        );
      } else {
        return `background-color: white; border-left:3px solid ${this.selected_report.main_color}; border-bottom: 1px solid ${this.selected_report.sub_color}; border-top: 0px; border-right: 0px;`;
      }
    },
    main_bg_color() {
      if (this.selected_report) {
        return `background-color:` + this.selected_report.main_color;
      } else return `background-color: #494949`;
    }
  },
  methods: {
    ...mapActions('report_contents', ['fetchSelectedTcfd']),
    navAnchor(event, anchor) {
      event.preventDefault();
      if (anchor && document.querySelector('#w' + anchor)) {
        const elem = document.querySelector('#w' + anchor);
        if (elem) {
          elem.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }
};
</script>
